import { relewiseRecommendations } from '@/shared/constants/constants';
import {
  attachRowArrows,
  createRecommendationElement,
  createSourceDecision,
  excludeProductFromTarget,
  isRecommendationType,
  onRecommendationsSettled,
  onSlotViewed,
  resolveRecommendationVariant,
  startSlotTracking,
  trackSlotViewed,
  waitForRelewise
} from '@/shared/instances/relewise-recommendations';

const { HIDDEN_CLASS, SOURCES, SECTION_TYPES, VARIANTS } =
  relewiseRecommendations;

const SELECTORS = {
  ROOT: '.js-relewise-recommendations',
  CONTENT: '.js-relewise-recommendations-content',
  WIDGET_CONTAINER: '.js-relewise-recommendations-widget'
} as const;

const initSlot = async (root: HTMLElement) => {
  const { type, location, flagKey, sectionId = '' } = root.dataset;
  const entityId = root.dataset.entityId ?? '';
  const count =
    Number(root.dataset.count) || relewiseRecommendations.DEFAULT_COUNT;
  const content = root.querySelector<HTMLElement>(SELECTORS.CONTENT);
  const widgetContainer = root.querySelector<HTMLElement>(
    SELECTORS.WIDGET_CONTAINER
  );

  if (
    !isRecommendationType(type) ||
    !location ||
    !flagKey ||
    !content ||
    !widgetContainer
  )
    return;

  const variant = await resolveRecommendationVariant(flagKey);
  if (!variant) return;

  const { decide: decideSource, decided: decidedSource } =
    createSourceDecision();

  onSlotViewed(root, async () => {
    trackSlotViewed({
      flagKey,
      variant,
      location,
      source: await decidedSource
    });
  });

  try {
    const useRelewise = variant === VARIANTS.TEST && (await waitForRelewise());
    decideSource(useRelewise ? SOURCES.RELEWISE : SOURCES.NONE);
    if (!useRelewise) return;

    const target = entityId ? `${type}-${sectionId}` : undefined;
    if (target) excludeProductFromTarget(target, entityId);

    const widget = createRecommendationElement({
      type,
      location,
      count,
      target,
      productId: entityId || undefined,
      scrollRow: true
    });

    onRecommendationsSettled(widget, (hasResults) => {
      if (!hasResults) return;
      content.classList.remove(HIDDEN_CLASS);
      startSlotTracking(content, sectionId, SECTION_TYPES.RELEWISE);
      attachRowArrows({
        container: widgetContainer,
        widget,
        template: root.querySelector<HTMLTemplateElement>(
          relewiseRecommendations.ARROWS_TEMPLATE_SELECTOR
        )
      });
    });
    widgetContainer.appendChild(widget);
  } catch (error) {
    // Fail silently
    console.error(error);
    decideSource(SOURCES.NONE);
  }
};

document
  .querySelectorAll<HTMLElement>(SELECTORS.ROOT)
  .forEach((root) => void initSlot(root));
